import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AppStatus,
    CodedReference,
    DocumentRequirement,
    DocumentRequirementCreate,
    DocumentRequirementType,
    SystemEntity,
} from '@wdx/clmi/clmi-swagger-gen';
import { Observable } from 'rxjs';
import { ConfigService } from '../../services/config.service';
import { pascalToCamelCase } from '@wdx/shared/utils';
import {
    DocumentrequirementBulkactionActioncodeApiService,
    DocumentrequirementtypeFilteredApiService,
} from '@wdx/clmi/clmi-swagger-gen/api-services';

@Injectable()
export class DocumentChecklistService {
    constructor(
        private http: HttpClient,
        private config: ConfigService,
        private documentRequirementBulkactionActioncodeApiService: DocumentrequirementBulkactionActioncodeApiService,
        private documentRequirementTypeFilteredApiService: DocumentrequirementtypeFilteredApiService,
    ) {}

    getDocumentRequirementsCategories(
        entityId: string,
        entityType: SystemEntity,
        data: string[] = [],
    ): Observable<DocumentRequirement[]> {
        return this.http.post<DocumentRequirement[]>(
            `${
                this.config.getConfiguration().API_BASE
            }/documentrequirement/${entityType}/${entityId}/categories`,
            data,
        );
    }

    getAssociatedDocuments(
        documentRequirementId: string,
    ): Observable<Document[]> {
        return this.http.get<Document[]>(
            `${
                this.config.getConfiguration().API_BASE
            }/documentrequirement/${documentRequirementId}/documents`,
        );
    }

    addDocumentRequirement(
        documentRequirementType: CodedReference,
        entity: SystemEntity,
        entityRef: CodedReference,
        relatedEntity?: SystemEntity,
        relatedEntityRef?: CodedReference,
        additionalEntities: {
            entity: SystemEntity;
            entityRef: CodedReference;
        }[] = [],
    ): Observable<DocumentRequirement> {
        const additionalEntityRefs = additionalEntities.reduce(
            (acc, { entity: additionalEntity, entityRef: additionalEntityRef }) =>
                additionalEntity && additionalEntityRef
                    ? {
                          ...acc,
                          [pascalToCamelCase(additionalEntity)]:
                              additionalEntityRef,
                      }
                    : acc,
            {},
        );
        const _body = {
            requirementType: documentRequirementType,
            ...(entity && entityRef
                ? { [pascalToCamelCase(entity)]: entityRef }
                : {}),
            ...(relatedEntity && relatedEntity !== entity && relatedEntityRef
                ? {
                      [pascalToCamelCase(relatedEntity)]: relatedEntityRef,
                  }
                : {}),
            ...additionalEntityRefs,
        };
        const body = new DocumentRequirementCreate(_body);
        return this.http.post(
            `${this.config.getConfiguration().API_BASE}/documentrequirement`,
            body,
        );
    }

    generateDocumentFromTemplate(
        payload: {
            documentRequirementId: string;
            documentTemplateId: string;
        }[],
    ): Observable<any> {
        return this.http.patch(
            `${
                this.config.getConfiguration().API_BASE
            }/documentrequirement/generate`,
            payload,
        );
    }

    approveDocumentRequirement(
        documentRequirementIds: string[],
        approve = true,
    ): Observable<any> {
        return this.documentRequirementBulkactionActioncodeApiService.patch(
            approve ? 'approve' : 'reject',
            documentRequirementIds,
        );
    }

    cancelDocumentRequirement(
        documentRequirementIds: string[],
    ): Observable<any> {
        return this.documentRequirementBulkactionActioncodeApiService.patch(
            'cancel',
            documentRequirementIds,
        );
    }

    deleteDocumentRequirement(documentRequirementId: string): Observable<any> {
        return this.http.delete(
            `${
                this.config.getConfiguration().API_BASE
            }/documentrequirement/${documentRequirementId}`,
        );
    }

    createDocumentExternalTask(
        documentRequirementId: string,
        externalUserPartyId: string,
    ): Observable<any> {
        return this.http.post(
            `${
                this.config.getConfiguration().API_BASE
            }/externaluser/${externalUserPartyId}/documentrequirement/${documentRequirementId}`,
            {},
        );
    }

    sendDocumentRequirement(documentRequirementIds: string[]): Observable<any> {
        return this.documentRequirementBulkactionActioncodeApiService.patch(
            'sent',
            documentRequirementIds,
        );
    }

    getDocumentRequirementTypes(
        app: AppStatus,
    ): Observable<DocumentRequirementType[]> {
        return this.documentRequirementTypeFilteredApiService.get(
            app?.viewEntity,
            app?.client?.id,
            app?.case?.id,
            true,
        );
    }

    linkDocumentToRequirement(
        documentRequirementId: string,
        documentId: string,
        isLinked = true,
    ): Observable<any> {
        if (isLinked) {
            return this.http.patch(
                `${
                    this.config.getConfiguration().API_BASE
                }/documentrequirement/${documentRequirementId}/document/${documentId}`,
                {},
            );
        } else {
            return this.http.delete(
                `${
                    this.config.getConfiguration().API_BASE
                }/documentrequirement/${documentRequirementId}/document/${documentId}`,
            );
        }
    }
}
